if (!customElements.get('smart-overview')) {
  customElements.define(
    'smart-overview',
    class SmartOverview extends HTMLElement {
      connectedCallback() {
        this.sid = this.dataset.sectionId;
        this.waNumber = this.dataset.waNumber || '';
        this.root = this.closest('product-info') || document;
        this.storage = {
          get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
          set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} },
          remove: (k) => { try { localStorage.removeItem(k); } catch (e) {} },
        };
        this.variants = this.readJSON('[data-variants]', []);
        this.current = this.readJSON('[data-current-product]', null);

        this.initZone();
        if (this.dataset.rememberSize === 'true') setTimeout(() => this.initSize(), 0);
        this.initSticky();
        this.initVariantWatch();
        this.initShipping();
        this.initSizeCalc();
        this.initWishlist();
        this.initShare();
        this.initRecent();
        this.update();
      }

      readJSON(selector, fallback) {
        const el = this.querySelector(selector);
        if (!el) return fallback;
        try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
      }

      setText(el, value) {
        if (el && el.textContent !== value) el.textContent = value;
      }

      form() {
        return document.getElementById(`product-form-${this.sid}`);
      }

      waLink(message) {
        return `https://wa.me/${this.waNumber}?text=${encodeURIComponent(message)}`;
      }

      /* ---------- Variante actual ---------- */
      currentVariantId() {
        const input = this.form()?.querySelector('input[name="id"]');
        const fromUrl = new URLSearchParams(window.location.search).get('variant');
        return Number((input && input.value) || fromUrl || 0);
      }

      currentVariant() {
        const id = this.currentVariantId();
        return this.variants.find((v) => v.id === id) || null;
      }

      variantLabel() {
        const parts = [];
        this.root.querySelectorAll('input[type="radio"]:checked').forEach((r) => parts.push(r.value));
        this.root.querySelectorAll('variant-selects select, variant-radios select').forEach((s) => parts.push(s.value));
        return parts.join(', ');
      }

      initVariantWatch() {
        let ticking = false;
        const schedule = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.update();
            this.refreshSticky();
          });
        };
        this.schedule = schedule;

        document.addEventListener('change', (e) => {
          const t = e.target;
          if (t && (t.name === 'id' || (t.closest && t.closest('variant-selects, variant-radios')))) schedule();
        });

        const watched = this.closest('.product__info-container') || this.root;
        new MutationObserver((mutations) => {
          if (mutations.every((m) => this.contains(m.target))) return;
          schedule();
        }).observe(watched, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      }

      /* ---------- Ahorro, cuotas y aviso de agotado ---------- */
      update() {
        const v = this.currentVariant();
        this.updatePriceBox(v);
        this.updateOos(v);
        this.updateAsk();
        this.paintShipping();
      }

      updatePriceBox(v) {
        const box = this.querySelector('[data-pricebox]');
        if (!box) return;
        const save = this.querySelector('[data-save]');
        const inst = this.querySelector('[data-inst]');
        const n = Number(this.dataset.installments) || 0;

        const saveText = v && v.save ? `Ahorrás ${v.save} (−${v.pct}%)` : '';
        this.setText(save, saveText);

        let instText = '';
        if (v && v.inst && n > 1) {
          instText = `o ${n} cuotas de ${v.inst}`;
          const note = this.dataset.installmentsNote;
          if (note) instText += ` — ${note}`;
        }
        this.setText(inst, instText);
        box.hidden = !(saveText || instText);
      }

      updateOos(v) {
        const panel = this.querySelector('[data-oos]');
        if (!panel) return;
        if (!v || v.available) {
          panel.hidden = true;
          return;
        }
        const label = this.variantLabel();
        this.setText(
          this.querySelector('[data-oos-text]'),
          label ? `Esta opción (${label}) está agotada.` : 'Este producto está agotado.'
        );
        const link = this.querySelector('[data-oos-wa]');
        if (link && this.waNumber) {
          const msg = `Hola, ¿me avisan cuando vuelva a haber stock de ${this.dataset.productTitle}${label ? ` (${label})` : ''}? ${this.dataset.productUrl}`;
          link.href = this.waLink(msg);
        }
        panel.hidden = false;
      }

      /* ---------- Entrega por zona y fecha estimada ---------- */
      addBusinessDays(start, days) {
        const d = new Date(start);
        let left = days;
        while (left > 0) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0) left--; // domingo no cuenta
        }
        return d;
      }

      formatDay(d) {
        try {
          return new Intl.DateTimeFormat('es-PY', { weekday: 'long', day: 'numeric', month: 'long' }).format(d);
        } catch (e) {
          return d.toLocaleDateString();
        }
      }

      initZone() {
        const select = this.querySelector('[data-zone-select]');
        const text = this.querySelector('[data-zone-text]');
        const eta = this.querySelector('[data-zone-eta]');
        if (!select || !text) return;

        const apply = () => {
          const opt = select.options[select.selectedIndex];
          text.textContent = opt ? opt.dataset.text || '' : '';
          if (!eta) return;
          const min = Number(opt && opt.dataset.min) || 0;
          const max = Number(opt && opt.dataset.max) || 0;
          if (opt && opt.value && max > 0) {
            const from = this.addBusinessDays(new Date(), Math.min(min || max, max));
            const to = this.addBusinessDays(new Date(), max);
            eta.textContent = from.toDateString() === to.toDateString()
              ? `Lo recibís el ${this.formatDay(to)}.`
              : `Lo recibís entre el ${this.formatDay(from)} y el ${this.formatDay(to)}.`;
            eta.hidden = false;
          } else {
            eta.hidden = true;
          }
        };

        const saved = this.storage.get('rostore_zone');
        if (saved && [...select.options].some((o) => o.value === saved)) select.value = saved;
        apply();

        select.addEventListener('change', () => {
          this.storage.set('rostore_zone', select.value);
          apply();
        });
      }

      /* ---------- Talla recordada ---------- */
      sizeControls() {
        const re = /talla|size|tama|n[uú]mero|calzado/i;
        const radios = [...this.root.querySelectorAll('input[type="radio"]')].filter((i) => re.test(i.name));
        const selects = [...this.root.querySelectorAll('select')].filter((s) => re.test(s.name || ''));
        return { radios, selects };
      }

      initSize() {
        const { radios, selects } = this.sizeControls();
        const note = this.querySelector('[data-size-note]');
        const saved = this.storage.get('rostore_size');

        const onChange = (value) => { if (value) this.storage.set('rostore_size', value); };
        radios.forEach((r) => r.addEventListener('change', () => r.checked && onChange(r.value)));
        selects.forEach((s) => s.addEventListener('change', () => onChange(s.value)));

        if (!saved) return;

        let applied = false;
        const radio = radios.find((r) => r.value === saved && !r.checked && !r.disabled && !r.classList.contains('disabled'));
        if (radio) {
          radio.click();
          applied = true;
        } else {
          const sel = selects.find((s) => [...s.options].some((o) => o.value === saved && !o.disabled));
          if (sel && sel.value !== saved) {
            sel.value = saved;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            applied = true;
          }
        }

        if (applied && note) {
          note.textContent = `Elegimos tu talla habitual: ${saved}.`;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.textContent = 'Olvidar';
          btn.addEventListener('click', () => {
            this.storage.remove('rostore_size');
            note.hidden = true;
          });
          note.appendChild(btn);
          note.hidden = false;
        }
      }

      /* ---------- Listas de productos: recientes y favoritos ---------- */
      readList(key) {
        let list = [];
        try { list = JSON.parse(this.storage.get(key) || '[]'); } catch (e) { list = []; }
        return Array.isArray(list) ? list.filter((p) => p && p.handle) : [];
      }

      renderStrip(box, items) {
        const ul = box && box.querySelector('ul');
        if (!ul) return;
        ul.textContent = '';
        items.forEach((p) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = p.url;
          if (p.image) {
            const img = document.createElement('img');
            img.src = p.image;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 110;
            img.height = 110;
            a.appendChild(img);
          }
          const title = document.createElement('span');
          title.textContent = p.title;
          a.appendChild(title);
          if (p.price) {
            const price = document.createElement('span');
            price.className = 'smart-overview__recent-price';
            price.textContent = p.price;
            a.appendChild(price);
          }
          li.appendChild(a);
          ul.appendChild(li);
        });
        box.hidden = items.length === 0;
      }

      initRecent() {
        if (!this.current || !this.current.handle) return;
        const key = 'rostore_recent';
        const list = this.readList(key);
        this.renderStrip(this.querySelector('[data-recent]'), list.filter((p) => p.handle !== this.current.handle).slice(0, 6));
        const next = [this.current, ...list.filter((p) => p.handle !== this.current.handle)].slice(0, 8);
        this.storage.set(key, JSON.stringify(next));
      }

      initWishlist() {
        const btn = this.querySelector('[data-wish-toggle]');
        if (!btn || !this.current || !this.current.handle) return;
        const box = this.querySelector('[data-wishlist]');
        const label = btn.querySelector('[data-wish-label]');
        const key = 'rostore_wishlist';
        const paint = () => {
          const list = this.readList(key);
          const saved = list.some((p) => p.handle === this.current.handle);
          btn.setAttribute('aria-pressed', String(saved));
          this.setText(label, saved ? 'Guardado en favoritos' : 'Guardar en favoritos');
          this.renderStrip(box, list.filter((p) => p.handle !== this.current.handle).slice(0, 6));
        };
        btn.addEventListener('click', () => {
          const list = this.readList(key);
          const exists = list.some((p) => p.handle === this.current.handle);
          const next = exists
            ? list.filter((p) => p.handle !== this.current.handle)
            : [this.current, ...list].slice(0, 24);
          this.storage.set(key, JSON.stringify(next));
          paint();
        });
        paint();
      }

      /* ---------- Compartir y preguntas por WhatsApp ---------- */
      initShare() {
        const a = this.querySelector('[data-share-wa]');
        if (a) a.href = `https://wa.me/?text=${encodeURIComponent(`${this.dataset.productTitle}: ${this.dataset.productUrl}`)}`;
      }

      updateAsk() {
        const links = this.querySelectorAll('[data-ask]');
        if (!links.length || !this.waNumber) return;
        const label = this.variantLabel();
        const id = this.currentVariantId();
        const url = this.dataset.productUrl + (id ? `?variant=${id}` : '');
        links.forEach((a) => {
          const msg = `${a.dataset.ask} — ${this.dataset.productTitle}${label ? ` (${label})` : ''}: ${url}`;
          a.href = this.waLink(msg);
        });
      }

      /* ---------- Calculadora de talla ---------- */
      selectSizeValue(value) {
        const { radios, selects } = this.sizeControls();
        const norm = (x) => String(x).trim().toLowerCase();
        const radio = radios.find((r) => norm(r.value) === norm(value) && !r.disabled && !r.classList.contains('disabled'));
        if (radio) {
          if (!radio.checked) radio.click();
          return true;
        }
        for (const sel of selects) {
          const opt = [...sel.options].find((o) => norm(o.value) === norm(value) && !o.disabled);
          if (opt) {
            if (sel.value !== opt.value) {
              sel.value = opt.value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return true;
          }
        }
        return false;
      }

      initSizeCalc() {
        const src = this.querySelector('[data-size-chart]');
        const input = this.querySelector('[data-foot]');
        const out = this.querySelector('[data-size-result]');
        const pick = this.querySelector('[data-size-pick]');
        if (!src || !input || !out || !pick) return;

        const rows = src.textContent
          .split('\n')
          .map((l) => l.trim().match(/^([\d.,]+)\s*=\s*(.+)$/))
          .filter(Boolean)
          .map((m) => ({ cm: parseFloat(m[1].replace(',', '.')), size: m[2].trim() }))
          .filter((r) => !isNaN(r.cm))
          .sort((a, b) => a.cm - b.cm);

        let found = null;
        input.addEventListener('input', () => {
          const cm = parseFloat(String(input.value).replace(',', '.'));
          found = null;
          pick.hidden = true;
          if (isNaN(cm)) {
            out.textContent = '';
            return;
          }
          found = rows.find((r) => cm <= r.cm) || null;
          if (!found) {
            out.textContent = 'Tu medida supera nuestra tabla. Consultanos por WhatsApp.';
            return;
          }
          out.textContent = `Te recomendamos la talla ${found.size}.`;
          pick.hidden = false;
        });

        pick.addEventListener('click', () => {
          if (!found) return;
          if (!this.selectSizeValue(found.size)) {
            out.textContent = `Te recomendamos la talla ${found.size}, pero no está disponible en este producto.`;
          }
        });
      }

      /* ---------- Envío gratis según el carrito ---------- */
      money(units) {
        const cur = this.dataset.currency || '';
        const symbol = cur === 'PYG' ? 'Gs.' : cur;
        return `${symbol} ${new Intl.NumberFormat('es-PY').format(Math.round(units))}`.trim();
      }

      initShipping() {
        this.shipBox = this.querySelector('[data-ship]');
        const min = Number(this.dataset.freeMin) || 0;
        if (!this.shipBox || !min) return;

        const fetchCart = async () => {
          try {
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
            const cart = await res.json();
            this.cartCents = Number(cart.total_price) || 0;
            this.paintShipping();
          } catch (e) {}
        };
        fetchCart();

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) new MutationObserver(fetchCart).observe(bubble, { childList: true, subtree: true });
        document.addEventListener('submit', (e) => {
          if (e.target && e.target.id === `product-form-${this.sid}`) setTimeout(fetchCart, 1500);
        });
      }

      paintShipping() {
        if (!this.shipBox || this.cartCents === undefined) return;
        const min = Number(this.dataset.freeMin) || 0;
        if (!min) return;
        const cart = this.cartCents / 100;
        const left = Math.max(min - cart, 0);

        this.setText(
          this.querySelector('[data-ship-text]'),
          left > 0 ? `Te faltan ${this.money(left)} para el envío gratis.` : '¡Tenés envío gratis!'
        );
        const bar = this.querySelector('[data-ship-bar]');
        if (bar) bar.style.width = `${Math.min(100, (cart / min) * 100)}%`;

        const sub = this.querySelector('[data-ship-sub]');
        const v = this.currentVariant();
        if (sub) {
          if (v && v.cents && left > 0) {
            const after = Math.max(left - v.cents / 100, 0);
            this.setText(sub, after > 0
              ? `Con este producto te faltarían ${this.money(after)}.`
              : '¡Con este producto conseguís envío gratis!');
            sub.hidden = false;
          } else {
            sub.hidden = true;
          }
        }
        this.shipBox.hidden = false;
      }

      /* ---------- Barra fija en móvil ---------- */
      initSticky() {
        this.bar = this.querySelector('[data-sticky]');
        if (!this.bar) return;
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.variantEl = this.querySelector('[data-sticky-variant]');
        this.waEl = this.querySelector('[data-sticky-wa]');
        this.addBtn = this.querySelector('[data-sticky-add]');

        this.addBtn.addEventListener('click', () => {
          const btn = this.form()?.querySelector('[name="add"]');
          if (btn && !btn.disabled) btn.click();
        });

        let ticking = false;
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.refreshSticky();
          });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        this.refreshSticky();
      }

      refreshSticky() {
        if (!this.bar) return;
        const form = this.form();
        if (!form) return;

        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const pastForm = form.getBoundingClientRect().bottom < 0;
        const footer = document.querySelector('footer');
        const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight - 60 : false;
        this.bar.hidden = !(isMobile && pastForm && !footerVisible);
        if (this.bar.hidden) return;

        const priceNode = document.querySelector(`#price-${this.sid} .price-item--last`) ||
          document.querySelector(`#price-${this.sid} .price-item--regular`);
        this.setText(this.priceEl, priceNode ? priceNode.textContent.trim() : '');

        const label = this.variantLabel();
        this.setText(this.variantEl, label);

        const addNative = form.querySelector('[name="add"]');
        this.addBtn.disabled = !addNative || addNative.disabled;

        if (this.waEl && this.waNumber) {
          const idInput = form.querySelector('input[name="id"]');
          const url = this.dataset.productUrl + (idInput ? `?variant=${idInput.value}` : '');
          const msg = `Hola, quiero consultar por ${this.dataset.productTitle}${label ? ` (${label})` : ''}: ${url}`;
          this.waEl.href = this.waLink(msg);
        }
      }
    }
  );
}
